import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { API_CONFIG } from '../../../utils/apiConfig';

const InventoryPanel = () => {
  const [lowStockItems, setLowStockItems] = useState({ levelOne: [], levelTwo: [], noStock: [] });
  const [allInventory, setAllInventory] = useState([]);
  const [inventorySummary, setInventorySummary] = useState(null);
  const [criticalAlerts, setCriticalAlerts] = useState([]);
  const [restockForm, setRestockForm] = useState({
    itemCode: '',
    itemName: '',
    quantity: '',
    unitPrice: '',
    gst: '',
    hsn: '',
    originalQuantity: '',
    totalSold: '',
    salesVelocity: '',
    daysOfStock: '',
    stockStatus: '',
    alerts: '',
    salesBreakdown: ''
  });
  const [autoRestockSuggestions, setAutoRestockSuggestions] = useState([]);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const API_BASE_URL = API_CONFIG.BASE_URL;

  const getAuthToken = useCallback(async () => {
    if (!currentUser) {
      throw new Error('No authenticated user');
    }
    return await currentUser.getIdToken(true);
  }, [currentUser]);

  const fetchLowStockAlerts = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      const token = await getAuthToken();
      const res = await axios.get(`${API_BASE_URL}/api/inventory/alerts?threshold=5`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setLowStockItems({
        levelOne: res.data.levelOne || [],
        levelTwo: res.data.levelTwo || [],
        noStock: res.data.noStock || []
      });
    } catch (err) {
      console.error('Error fetching low stock items:', err);
      if (err.response?.status === 404) {
        console.warn('Inventory alerts endpoint not available on server');
        setLowStockItems({ levelOne: [], levelTwo: [], noStock: [] });
      }
    }
  }, [currentUser, getAuthToken, API_BASE_URL]);

  const fetchAllInventory = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      const token = await getAuthToken();
      const res = await axios.get(`${API_BASE_URL}/api/inventory/analysis`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      
      
      const inventoryData = res.data.data?.inventoryAnalysis || [];
      
      // Debug specific item (Purifier)
      const purifierItem = inventoryData.find(item => 
        item.itemCode === 'ITEM5' || (item.itemName && item.itemName.toLowerCase().includes('purifier'))
      );
      if (purifierItem) {
        console.log('🔍 PURIFIER DATA AFTER REFRESH:', {
          itemCode: purifierItem.itemCode,
          itemName: purifierItem.itemName,
          availableQuantity: purifierItem.availableQuantity,
          currentStock: purifierItem.currentStock,
          totalSold: purifierItem.totalSold,
          stockStatus: purifierItem.stockStatus
        });
      } else {
        console.log('❌ PURIFIER NOT FOUND IN FRESH DATA');
      }
      
      setAllInventory(inventoryData);
      setInventorySummary(res.data.data?.summary || null);
      setCriticalAlerts(res.data.data?.criticalAlerts || []);
    } catch (err) {
      console.error('Error fetching inventory analysis:', err);
      if (err.response?.status === 404) {
        console.warn('Inventory analysis endpoint not available, trying alternative endpoint...');
        
        // Fallback to unified inventory analysis
        try {
          const fallbackToken = await getAuthToken();
          const fallbackRes = await axios.get(`${API_BASE_URL}/api/inventory/unified-analysis`, {
            headers: { Authorization: `Bearer ${fallbackToken}` },
          });
          setAllInventory(fallbackRes.data.data?.inventoryAnalysis || []);
          setInventorySummary(fallbackRes.data.data?.summary || null);
          setCriticalAlerts(fallbackRes.data.data?.criticalAlerts || []);
        } catch (fallbackErr) {
          setAllInventory([]);
        }
      } else {
        setAllInventory([]);
      }
    }
  }, [currentUser, getAuthToken, API_BASE_URL]);

  const handleRestockSubmit = async (e) => {
    e.preventDefault();
    try {
      
      const token = await getAuthToken();
      
      const response = await axios.post(`${API_BASE_URL}/api/inventory/restock`, restockForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      
      // Show smart restock details
      const restockData = response.data.data;
      if (restockData.smartRestockApplied) {
        alert(`🧠 Smart Restock Applied!\n\n` +
              `📦 Product: ${restockData.itemName}\n` +
              `📊 Minimum Monthly Sales: ${restockData.minimumMonthlySales}\n` +
              `💰 Requested Restock: ${restockData.requestedRestockQuantity}\n` +
              `✅ Actual Restock: ${restockData.actualRestockQuantity}\n` +
              `📈 Final Quantity: ${restockData.currentBalance}\n\n` +
              `💡 Smart restocking prevented overstocking!`);
      } else {
        alert(`✅ Inventory restocked successfully!\n\n` +
              `📦 Product: ${restockData.itemName}\n` +
              `📊 Minimum Monthly Sales: ${restockData.minimumMonthlySales}\n` +
              `📈 Final Quantity: ${restockData.currentBalance}`);
      }
      
      // Reset the form
      setRestockForm({ 
        itemCode: '', 
        itemName: '', 
        quantity: '', 
        unitPrice: '', 
        gst: '', 
        hsn: '', 
        originalQuantity: '', 
        totalSold: '', 
        salesVelocity: '', 
        daysOfStock: '', 
        stockStatus: '', 
        alerts: '', 
        salesBreakdown: '' 
      });
      
      
      // Show loading state
      const loadingAlert = document.createElement('div');
      loadingAlert.innerHTML = `
        <div style="
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: #007bff;
          color: white;
          padding: 20px;
          border-radius: 8px;
          z-index: 9999;
          font-size: 16px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        ">
          🔄 Refreshing inventory data...
        </div>
      `;
      document.body.appendChild(loadingAlert);
      
      try {
        await fetchAllInventory();
        await fetchLowStockAlerts();
        
        // Remove loading indicator
        document.body.removeChild(loadingAlert);
        
        // Show success message
        alert('✅ Inventory updated successfully! The table has been refreshed with the latest data.');
      } catch (refreshError) {
        document.body.removeChild(loadingAlert);
        alert('⚠️ Inventory restocked but failed to refresh the table. Please refresh the page to see updated quantities.');
      }
    } catch (err) {
      console.error('❌ Error restocking:', err);
      console.error('Error details:', err.response?.data || err.message);
      
      if (err.code === 'ERR_CONNECTION_REFUSED') {
        alert('❌ Cannot connect to server. Please check if the server is running on port 5000.');
      } else if (err.response?.status === 401) {
        alert('❌ Authentication failed. Please log in again.');
      } else if (err.response?.status === 500) {
        alert('❌ Server error: ' + (err.response?.data?.message || 'Unknown server error'));
      } else {
        alert('❌ Failed to restock inventory: ' + (err.response?.data?.message || err.message));
      }
    }
  };

  // Calculate automatic restock suggestions based on sales analysis
  const calculateAutoRestockSuggestions = useCallback(async () => {
    if (!currentUser || allInventory.length === 0) return;
    
    try {
      
      const suggestions = [];
      
      for (const item of allInventory) {
        // Calculate minimum monthly sales from sales breakdown and monthly sales
        let minimumMonthlySales = 5; // default fallback
        
        if (item.monthlySales && Object.keys(item.monthlySales).length > 0) {
          const monthlyTotals = Object.values(item.monthlySales).map(month => month.total);
          minimumMonthlySales = Math.max(...monthlyTotals);
        } else if (item.salesBreakdown) {
          // Estimate from total sales breakdown
          const totalSales = (item.salesBreakdown.cashBills || 0) + 
                           (item.salesBreakdown.creditBills || 0) + 
                           (item.salesBreakdown.manualEntries || 0);
          minimumMonthlySales = Math.max(1, Math.ceil(totalSales / 3)); // estimate monthly average
        }
        
        const currentStock = item.availableQuantity || 0;
        const neededQuantity = Math.max(0, minimumMonthlySales - currentStock);
        
        if (neededQuantity > 0) {
          suggestions.push({
            itemCode: item.itemCode,
            itemName: item.itemName,
            currentStock,
            minimumMonthlySales,
            suggestedRestock: neededQuantity,
            unitPrice: item.unitPrice || 0,
            gst: item.gst || 0,
            hsn: item.hsn || '',
            stockStatus: item.stockStatus,
            priority: currentStock === 0 ? 'CRITICAL' : 
                     currentStock <= 2 ? 'HIGH' : 
                     currentStock <= minimumMonthlySales * 0.5 ? 'MEDIUM' : 'LOW'
          });
        }
      }
      
      // Sort by priority (CRITICAL > HIGH > MEDIUM > LOW)
      suggestions.sort((a, b) => {
        const priorityOrder = { 'CRITICAL': 4, 'HIGH': 3, 'MEDIUM': 2, 'LOW': 1 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      });
      
      setAutoRestockSuggestions(suggestions);
      
    } catch (error) {
    }
  }, [currentUser, allInventory]);

  useEffect(() => {
    if (currentUser) {
      fetchLowStockAlerts();
      fetchAllInventory();
    }
  }, [fetchLowStockAlerts, fetchAllInventory, currentUser]);

  // Calculate auto restock suggestions when inventory data changes
  useEffect(() => {
    if (allInventory.length > 0) {
      calculateAutoRestockSuggestions();
    }
  }, [allInventory, calculateAutoRestockSuggestions]);

  const prefillRestockForm = (product) => {
    setRestockForm({
      itemCode: product.itemCode || '',
      itemName: product.itemName || '',
      quantity: '', // Leave empty for new restock quantity
      unitPrice: product.unitPrice || '',
      gst: product.gst || '',
      hsn: product.hsn || '',
      originalQuantity: product.availableQuantity || product.quantity || '',
      totalSold: product.totalSold || '',
      salesVelocity: product.salesVelocity || '',
      daysOfStock: product.daysOfStock || '',
      stockStatus: product.stockStatus || '',
      alerts: product.alerts ? product.alerts.map(alert => alert.message).join(', ') : '',
      salesBreakdown: product.salesBreakdown ? 
        `Cash: ${product.salesBreakdown.cashBills || 0}, Credit: ${product.salesBreakdown.creditBills || 0}, Manual: ${product.salesBreakdown.manualEntries || 0}` : ''
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="inventory-panel">
      <style>
        {`
          /* Responsive CSS for Inventory Table */
          @media (max-width: 768px) {
            .desktop-table {
              display: none !important;
            }
            .mobile-cards {
              display: block !important;
            }
          }
          
          @media (min-width: 769px) {
            .desktop-table {
              display: block !important;
            }
            .mobile-cards {
              display: none !important;
            }
          }
          
          /* Make table horizontally scrollable on tablets */
          @media (max-width: 1024px) and (min-width: 769px) {
            .desktop-table table {
              overflow-x: auto;
              display: block;
              white-space: nowrap;
            }
            .desktop-table thead,
            .desktop-table tbody,
            .desktop-table tr {
              display: table;
              width: 100%;
              table-layout: fixed;
            }
            .desktop-table th,
            .desktop-table td {
              min-width: 100px;
              padding: 6px;
              font-size: 11px;
            }
          }
        `}
      </style>
      <button className="bck-button" onClick={() => navigate(-1)}>← Back</button>
      <h2>📦 Inventory Management</h2>

      {inventorySummary && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '15px', 
          marginBottom: '2rem',
          padding: '15px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          <div style={{ textAlign: 'center', padding: '10px', backgroundColor: 'white', borderRadius: '5px' }}>
            <h4 style={{ margin: '0 0 5px 0', color: '#333' }}>Total Products</h4>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff' }}>
              {inventorySummary.totalProducts}
            </div>
          </div>
          <div style={{ textAlign: 'center', padding: '10px', backgroundColor: 'white', borderRadius: '5px' }}>
            <h4 style={{ margin: '0 0 5px 0', color: '#333' }}>Out of Stock</h4>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc3545' }}>
              {inventorySummary.outOfStock}
            </div>
          </div>
          <div style={{ textAlign: 'center', padding: '10px', backgroundColor: 'white', borderRadius: '5px' }}>
            <h4 style={{ margin: '0 0 5px 0', color: '#333' }}>Low Stock</h4>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#fd7e14' }}>
              {inventorySummary.lowStock}
            </div>
          </div>
          <div style={{ textAlign: 'center', padding: '10px', backgroundColor: 'white', borderRadius: '5px' }}>
            <h4 style={{ margin: '0 0 5px 0', color: '#333' }}>Rapid Depletion</h4>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ffc107' }}>
              {inventorySummary.rapidDepletion}
            </div>
          </div>
          <div style={{ textAlign: 'center', padding: '10px', backgroundColor: 'white', borderRadius: '5px' }}>
            <h4 style={{ margin: '0 0 5px 0', color: '#333' }}>Total Alerts</h4>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: inventorySummary.totalAlerts > 0 ? '#dc3545' : '#28a745' }}>
              {inventorySummary.totalAlerts}
            </div>
          </div>
          <div style={{ textAlign: 'center', padding: '10px', backgroundColor: 'white', borderRadius: '5px' }}>
            <h4 style={{ margin: '0 0 5px 0', color: '#333' }}>Value at Risk</h4>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#dc3545' }}>
              ₹{inventorySummary.totalValueAtRisk?.toFixed(2) || '0.00'}
            </div>
          </div>
        </div>
      )}

      {/* Critical Alerts Section */}
      {criticalAlerts && criticalAlerts.length > 0 && (
        <div style={{ 
          marginBottom: '2rem',
          padding: '15px',
          backgroundColor: '#ffebee',
          borderRadius: '8px',
          border: '2px solid #f44336'
        }}>
          <h3 style={{ color: '#d32f2f', margin: '0 0 15px 0' }}>🚨 Critical Inventory Alerts</h3>
          <div style={{ display: 'grid', gap: '10px' }}>
            {criticalAlerts.map((alert, index) => (
              <div key={index} style={{
                padding: '10px',
                backgroundColor: 'white',
                borderRadius: '5px',
                border: '1px solid #ffcdd2',
                color: '#d32f2f',
                fontWeight: 'bold'
              }}>
                {alert.message}
              </div>
            ))}
          </div>
        </div>
      )}

      <h3 style={{ textAlign: 'center', marginBlockEnd: '1rem' }}>➕ Restock Inventory</h3>
      <form onSubmit={handleRestockSubmit} className="restock-form">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', marginBottom: '20px' }}>
          <input type="text" placeholder="Item Code" value={restockForm.itemCode} onChange={(e) => setRestockForm({ ...restockForm, itemCode: e.target.value })} required />
          <input type="text" placeholder="Item Name" value={restockForm.itemName} onChange={(e) => setRestockForm({ ...restockForm, itemName: e.target.value })} required />
          <input type="number" placeholder="New Restock Quantity" value={restockForm.quantity} onChange={(e) => setRestockForm({ ...restockForm, quantity: e.target.value })} required />
          <input type="number" placeholder="Unit Price" value={restockForm.unitPrice} onChange={(e) => setRestockForm({ ...restockForm, unitPrice: e.target.value })} required />
          <input type="number" placeholder="GST %" value={restockForm.gst} onChange={(e) => setRestockForm({ ...restockForm, gst: e.target.value })} required />
          <input type="text" placeholder="HSN Code" value={restockForm.hsn} onChange={(e) => setRestockForm({ ...restockForm, hsn: e.target.value })} />
        </div>
        
        {/* Display Inventory Information (Read-only) */}
        <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #dee2e6' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#495057' }}>📊 Current Inventory Information</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', fontSize: '14px' }}>
            <div>
              <strong>Original Quantity:</strong> {restockForm.originalQuantity || 'N/A'}
            </div>
            <div>
              <strong>Total Sold:</strong> {restockForm.totalSold || 'N/A'}
            </div>
            <div>
              <strong>Sales Velocity:</strong> {restockForm.salesVelocity || 'N/A'} /day
            </div>
            <div>
              <strong>Days of Stock:</strong> {restockForm.daysOfStock === '999' ? '∞' : restockForm.daysOfStock || 'N/A'}
            </div>
            <div>
              <strong>Stock Status:</strong> 
              <span style={{ 
                color: restockForm.stockStatus === 'OUT_OF_STOCK' ? 'red' : 
                       restockForm.stockStatus === 'LOW_STOCK' ? 'orange' : 
                       restockForm.stockStatus === 'MEDIUM_STOCK' ? '#ffc107' : 'green',
                fontWeight: 'bold'
              }}>
                {restockForm.stockStatus === 'OUT_OF_STOCK' ? '🚨 OUT' : 
                 restockForm.stockStatus === 'LOW_STOCK' ? '⚠️ LOW' : 
                 restockForm.stockStatus === 'MEDIUM_STOCK' ? '⚡ MED' : 
                 restockForm.stockStatus ? '✅ GOOD' : 'N/A'}
              </span>
            </div>
            <div>
              <strong>Sales Breakdown:</strong> {restockForm.salesBreakdown || 'N/A'}
            </div>
          </div>
          {restockForm.alerts && (
            <div style={{ marginTop: '10px', padding: '8px', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffeaa7' }}>
              <strong style={{ color: '#856404' }}>⚠️ Alerts:</strong> 
              <span style={{ color: '#856404', fontSize: '12px' }}> {restockForm.alerts}</span>
            </div>
          )}
        </div>
        
        <button type="submit" style={{ 
          backgroundColor: '#28a745', 
          color: 'white', 
          padding: '10px 20px', 
          border: 'none', 
          borderRadius: '5px', 
          cursor: 'pointer',
          fontSize: '16px',
          fontWeight: 'bold'
        }}>➕ Restock Product</button>
      </form>

      {/* Section: Automatic Smart Restock Suggestions */}
      {autoRestockSuggestions.length > 0 && (
        <div style={{ marginTop: '2rem' }}>
          <h3>🧠 Automatic Smart Restock Suggestions</h3>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px', fontStyle: 'italic' }}>
            💡 <strong>Smart Analysis:</strong> Calculated based on monthly sales data to prevent overstocking
          </p>
          
          <div style={{ display: 'grid', gap: '15px' }}>
            {autoRestockSuggestions.map((suggestion, index) => (
              <div key={suggestion.itemCode} style={{
                backgroundColor: '#fff',
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '15px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                borderLeft: suggestion.priority === 'CRITICAL' ? '5px solid #dc3545' :
                           suggestion.priority === 'HIGH' ? '5px solid #fd7e14' :
                           suggestion.priority === 'MEDIUM' ? '5px solid #ffc107' : '5px solid #28a745'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                    {suggestion.itemName} ({suggestion.itemCode})
                  </h4>
                  <span style={{ 
                    fontSize: '12px', 
                    padding: '4px 8px', 
                    borderRadius: '4px', 
                    backgroundColor: suggestion.priority === 'CRITICAL' ? '#dc3545' :
                                   suggestion.priority === 'HIGH' ? '#fd7e14' :
                                   suggestion.priority === 'MEDIUM' ? '#ffc107' : '#28a745',
                    color: 'white',
                    fontWeight: 'bold'
                  }}>
                    {suggestion.priority}
                  </span>
                </div>
                
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                  gap: '10px', 
                  marginBottom: '10px',
                  fontSize: '14px'
                }}>
                  <div><strong>Current Stock:</strong> {suggestion.currentStock}</div>
                  <div><strong>Min Monthly Sales:</strong> {suggestion.minimumMonthlySales}</div>
                  <div><strong>Suggested Restock:</strong> <span style={{ color: '#007bff', fontWeight: 'bold' }}>{suggestion.suggestedRestock}</span></div>
                  <div><strong>Target Quantity:</strong> {suggestion.minimumMonthlySales}</div>
                </div>
                
                <button 
                  onClick={() => {
                    setRestockForm({
                      itemCode: suggestion.itemCode,
                      itemName: suggestion.itemName,
                      quantity: suggestion.suggestedRestock.toString(),
                      unitPrice: suggestion.unitPrice.toString(),
                      gst: suggestion.gst.toString(),
                      hsn: suggestion.hsn,
                      originalQuantity: suggestion.currentStock.toString(),
                      totalSold: '',
                      salesVelocity: '',
                      daysOfStock: '',
                      stockStatus: suggestion.stockStatus,
                      alerts: '',
                      salesBreakdown: ''
                    });
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  🧠 Apply Smart Restock ({suggestion.suggestedRestock} units)
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section: Inventory Analysis with Alerts */}
      <div style={{ marginTop: '2rem' }}>
        <h3>📊 Inventory Analysis & Alerts</h3>
        <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px', fontStyle: 'italic' }}>
          💡 <strong>Calculation:</strong> Current Stock = Original Quantity - Total Sold
        </p>
        
        {allInventory.length === 0 ? (
          <p>No inventory data available.</p>
        ) : (
          <div>
            <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
              📊 Showing {allInventory.length} inventory items
            </p>
            
            {/* Desktop Table View */}
            <div style={{ display: 'block' }} className="desktop-table">
              <table border="1" cellPadding="8" style={{ 
                fontSize: '12px', 
                width: '100%',
                borderCollapse: 'collapse',
                overflowX: 'auto'
              }}>
              <thead>
                <tr>
                  <th>Item Code</th>
                  <th>Name</th>
                  <th>Original Qty</th>
                  <th>Current Stock</th>
                  <th>Total Sold</th>
                  <th>Stock Status</th>
                  <th>Sales Velocity</th>
                  <th>Days Left</th>
                  <th>Unit Price</th>
                  <th>GST %</th>
                  <th>Alerts</th>
                  <th>Sales Breakdown</th>
                  <th>Restock</th>
                </tr>
              </thead>
              <tbody>
              {allInventory.map(item => {
                // Debug logging for each item
                console.log(`Inventory Item: ${item.itemName}`, {
                  itemCode: item.itemCode,
                  availableQuantity: item.availableQuantity,
                  currentStock: item.currentStock,
                  totalSold: item.totalSold,
                  stockStatus: item.stockStatus
                });
                
                return (
                <tr key={item.productId} style={{ 
                  backgroundColor: item.stockStatus === 'OUT_OF_STOCK' ? '#ffebee' : 
                                  item.stockStatus === 'LOW_STOCK' ? '#fff3cd' : 
                                  item.stockStatus === 'MEDIUM_STOCK' ? '#e8f5e8' : 'white',
                  color: item.stockStatus === 'OUT_OF_STOCK' ? 'red' : 'black'
                }}>
                  <td>{item.itemCode}</td>
                  <td>{item.itemName}</td>
                  <td style={{ fontWeight: 'bold' }}>{item.availableQuantity || 0}</td>
                  <td style={{ 
                    fontWeight: 'bold',
                    color: item.stockStatus === 'OUT_OF_STOCK' ? 'red' : 
                           item.stockStatus === 'LOW_STOCK' ? 'orange' : 
                           item.stockStatus === 'MEDIUM_STOCK' ? 'yellow' : 'green'
                  }}>
                    {item.currentStock || 0}
                  </td>
                  <td style={{ color: 'blue' }}>{item.totalSold || 0}</td>
                  <td style={{ 
                    fontWeight: 'bold',
                    color: item.stockStatus === 'OUT_OF_STOCK' ? 'red' : 
                           item.stockStatus === 'LOW_STOCK' ? 'orange' : 
                           item.stockStatus === 'MEDIUM_STOCK' ? '#ffc107' : 'green'
                  }}>
                    {item.stockStatus === 'OUT_OF_STOCK' ? '🚨 OUT' : 
                     item.stockStatus === 'LOW_STOCK' ? '⚠️ LOW' : 
                     item.stockStatus === 'MEDIUM_STOCK' ? '⚡ MED' : '✅ GOOD'}
                  </td>
                  <td>{item.salesVelocity || 0}/day</td>
                  <td style={{ 
                    color: item.daysOfStock <= 7 ? 'red' : 
                           item.daysOfStock <= 30 ? 'orange' : 'green'
                  }}>
                    {item.daysOfStock === 999 ? '∞' : item.daysOfStock}
                  </td>
                  <td>₹{item.unitPrice || 0}</td>
                  <td>{item.gst || 0}%</td>
                  <td>
                    {item.alerts && item.alerts.length > 0 ? (
                      <div style={{ fontSize: '10px' }}>
                        {item.alerts.map((alert, idx) => (
                          <div key={idx} style={{ 
                            color: alert.severity === 'HIGH' ? 'red' : 'orange',
                            marginBottom: '2px'
                          }}>
                            {alert.message}
                          </div>
                        ))}
                      </div>
                    ) : '✅ OK'}
                  </td>
                  <td style={{ fontSize: '10px' }}>
                    <div><strong>Total:</strong></div>
                    <div>Cash: {item.salesBreakdown?.cashBills || 0}</div>
                    <div>Credit: {item.salesBreakdown?.creditBills || 0}</div>
                    <div>Manual: {item.salesBreakdown?.manualEntries || 0}</div>
                    {item.monthlySales && Object.keys(item.monthlySales).length > 0 && (
                      <div style={{ marginTop: '5px', borderTop: '1px solid #ccc', paddingTop: '5px' }}>
                        <div><strong>Monthly:</strong></div>
                        {Object.entries(item.monthlySales).slice(0, 3).map(([month, data]) => (
                          <div key={month} style={{ fontSize: '9px' }}>
                            {month}: {data.total} ({data.cashBills}C/{data.creditBills}Cr)
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td><button onClick={() => prefillRestockForm(item)}>Restock</button></td>
                </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          
          {/* Mobile/Tablet Card View */}
          <div style={{ display: 'none' }} className="mobile-cards">
            {allInventory.map(item => {
              const currentStock = item.availableQuantity || 0;
              const stockStatus = currentStock === 0 ? 'Out of Stock' : 
                                 currentStock <= 2 ? 'Critical' : 
                                 currentStock <= 5 ? 'Low' : 'Good';
              const statusColor = stockStatus === 'Good' ? '#28a745' : 
                                 stockStatus === 'Low' ? '#ffc107' : 
                                 stockStatus === 'Critical' ? '#fd7e14' : '#dc3545';
              
              return (
                <div key={item.itemCode} style={{
                  backgroundColor: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '15px',
                  marginBottom: '15px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  {/* Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                      {item.itemName}
                    </h4>
                    <span style={{ 
                      fontSize: '12px', 
                      padding: '4px 8px', 
                      borderRadius: '4px', 
                      backgroundColor: statusColor, 
                      color: 'white',
                      fontWeight: 'bold'
                    }}>
                      {stockStatus}
                    </span>
                  </div>
                  
                  {/* Item Code */}
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                    <strong>Code:</strong> {item.itemCode}
                  </div>
                  
                  {/* Stock Info Grid */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr', 
                    gap: '10px', 
                    marginBottom: '10px',
                    fontSize: '14px'
                  }}>
                    <div>
                      <strong>Original Qty:</strong> {item.availableQuantity || 0}
                    </div>
                    <div>
                      <strong>Current Stock:</strong> {currentStock}
                    </div>
                    <div>
                      <strong>Total Sold:</strong> {item.totalSold || 0}
                    </div>
                    <div>
                      <strong>Unit Price:</strong> ₹{item.unitPrice || 0}
                    </div>
                  </div>
                  
                  {/* Sales Info */}
                  <div style={{ fontSize: '12px', marginBottom: '10px' }}>
                    <div><strong>Sales Velocity:</strong> {item.salesVelocity || 'N/A'}</div>
                    <div><strong>Days Left:</strong> {item.daysOfStock || 'N/A'}</div>
                    <div><strong>GST:</strong> {item.gst || 0}%</div>
                  </div>
                  
                  {/* Sales Breakdown */}
                  {item.salesBreakdown && (
                    <div style={{ fontSize: '12px', marginBottom: '10px' }}>
                      <strong>Sales Breakdown:</strong>
                      <div style={{ marginLeft: '10px' }}>
                        <div>Cash: {item.salesBreakdown.cashBills || 0}</div>
                        <div>Credit: {item.salesBreakdown.creditBills || 0}</div>
                        <div>Manual: {item.salesBreakdown.manualEntries || 0}</div>
                      </div>
                    </div>
                  )}
                  
                  {/* Alerts */}
                  {item.alerts && item.alerts.length > 0 && (
                    <div style={{ fontSize: '12px', marginBottom: '10px' }}>
                      <strong>Alerts:</strong>
                      <div style={{ marginLeft: '10px', color: '#dc3545' }}>
                        {item.alerts.join(', ')}
                      </div>
                    </div>
                  )}
                  
                  {/* Restock Button */}
                  <button 
                    onClick={() => prefillRestockForm(item)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    Restock
                  </button>
                </div>
              );
            })}
          </div>
          </div>
        )}
      </div>

      {/* Section: Monthly Sales Analysis */}
      <div style={{ marginTop: '2rem' }}>
        <h3>📊 Monthly Sales Analysis</h3>
        {allInventory && allInventory.length > 0 ? (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {allInventory
              .filter(item => item.monthlySales && Object.keys(item.monthlySales).length > 0)
              .slice(0, 10) // Show top 10 items with monthly sales data
              .map(item => (
                <div key={item.productId} style={{ 
                  border: '1px solid #ddd', 
                  padding: '10px', 
                  borderRadius: '5px',
                  backgroundColor: '#f9f9f9'
                }}>
                  <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>
                    {item.itemName} ({item.itemCode})
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                    {Object.entries(item.monthlySales).slice(0, 6).map(([month, data]) => (
                      <div key={month} style={{ 
                        padding: '8px', 
                        backgroundColor: '#fff', 
                        border: '1px solid #eee',
                        borderRadius: '3px'
                      }}>
                        <div style={{ fontWeight: 'bold', fontSize: '12px', color: '#666' }}>
                          {month}
                        </div>
                        <div style={{ fontSize: '11px' }}>
                          <div>Total: {data.total}</div>
                          <div>Cash: {data.cashBills}</div>
                          <div>Credit: {data.creditBills}</div>
                          {data.manualEntries > 0 && <div>Manual: {data.manualEntries}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <p>No monthly sales data available.</p>
        )}
      </div>

      {/* Section: Low Stock Items */}
      <div style={{ marginTop: '2rem' }}>
        <h3>⚠️ Low Stock Items (2-5)</h3>
        {lowStockItems.levelOne.length === 0 ? (
          <p>None</p>
        ) : (
          <div>
            {/* Desktop Table */}
            <div className="desktop-table">
              <table border="1" cellPadding="8" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th>Item Code</th>
                    <th>Name</th>
                    <th>Qty</th>
                    <th>Price</th>
                    <th>GST</th>
                    <th>Total Value</th>
                    <th>Restock</th>
                  </tr>
                </thead>
                <tbody>
              {lowStockItems.levelOne.map(item => (
                <tr key={item._id}>
                  <td>{item.itemCode}</td>
                  <td>{item.itemName}</td>
                  <td style={{ color: item.quantity < 0 ? 'red' : 'black' }}>{item.quantity}</td>
                  <td>{item.unitPrice}</td>
                  <td>{item.gst}%</td>
                  <td>₹{((item.unitPrice * item.quantity * (1 + item.gst / 100)).toFixed(2))}</td>
                  <td><button onClick={() => prefillRestockForm(item)}>Restock</button></td>
                </tr>
              ))}
                </tbody>
              </table>
            </div>
            
            {/* Mobile Cards for Low Stock */}
            <div className="mobile-cards">
              {lowStockItems.levelOne.map(item => (
                <div key={item._id} style={{
                  backgroundColor: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '15px',
                  marginBottom: '15px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                      {item.itemName}
                    </h4>
                    <span style={{ 
                      fontSize: '12px', 
                      padding: '4px 8px', 
                      borderRadius: '4px', 
                      backgroundColor: '#ffc107', 
                      color: 'white',
                      fontWeight: 'bold'
                    }}>
                      Low Stock
                    </span>
                  </div>
                  
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                    <strong>Code:</strong> {item.itemCode}
                  </div>
                  
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr', 
                    gap: '10px', 
                    marginBottom: '10px',
                    fontSize: '14px'
                  }}>
                    <div><strong>Quantity:</strong> {item.quantity}</div>
                    <div><strong>Price:</strong> ₹{item.unitPrice}</div>
                    <div><strong>GST:</strong> {item.gst}%</div>
                    <div><strong>Total Value:</strong> ₹{((item.unitPrice * item.quantity * (1 + item.gst / 100)).toFixed(2))}</div>
                  </div>
                  
                  <button 
                    onClick={() => prefillRestockForm(item)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    Restock
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Section: Critical Stock */}
      <div style={{ marginTop: '2rem' }}>
        <h3>🔴 Critically Low Stock (&lt; 2)</h3>
        {lowStockItems.levelTwo.length === 0 ? (
          <p>None</p>
        ) : (
          <div>
            {/* Desktop Table */}
            <div className="desktop-table">
              <table border="1" cellPadding="8" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th>Item Code</th>
                    <th>Name</th>
                    <th>Qty</th>
                    <th>Price</th>
                    <th>GST</th>
                    <th>Total Value</th>
                    <th>Restock</th>
                  </tr>
                </thead>
                <tbody>
                  {lowStockItems.levelTwo.map(item => (
                    <tr key={item._id}>
                      <td>{item.itemCode}</td>
                      <td>{item.itemName}</td>
                      <td style={{ color: item.quantity < 0 ? 'red' : 'black' }}>{item.quantity}</td>
                      <td>{item.unitPrice}</td>
                      <td>{item.gst}%</td>
                      <td>₹{((item.unitPrice * item.quantity * (1 + item.gst / 100)).toFixed(2))}</td>
                      <td><button onClick={() => prefillRestockForm(item)}>Restock</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Mobile Cards for Critical Stock */}
            <div className="mobile-cards">
              {lowStockItems.levelTwo.map(item => (
                <div key={item._id} style={{
                  backgroundColor: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '15px',
                  marginBottom: '15px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                      {item.itemName}
                    </h4>
                    <span style={{ 
                      fontSize: '12px', 
                      padding: '4px 8px', 
                      borderRadius: '4px', 
                      backgroundColor: '#dc3545', 
                      color: 'white',
                      fontWeight: 'bold'
                    }}>
                      Critical
                    </span>
                  </div>
                  
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                    <strong>Code:</strong> {item.itemCode}
                  </div>
                  
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr', 
                    gap: '10px', 
                    marginBottom: '10px',
                    fontSize: '14px'
                  }}>
                    <div><strong>Quantity:</strong> <span style={{ color: item.quantity < 0 ? 'red' : 'black' }}>{item.quantity}</span></div>
                    <div><strong>Price:</strong> ₹{item.unitPrice}</div>
                    <div><strong>GST:</strong> {item.gst}%</div>
                    <div><strong>Total Value:</strong> ₹{((item.unitPrice * item.quantity * (1 + item.gst / 100)).toFixed(2))}</div>
                  </div>
                  
                  <button 
                    onClick={() => prefillRestockForm(item)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    Restock
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Section: No Stock */}
      <div style={{ marginTop: '2rem' }}>
        <h3>🚫 No Stock Products (Missing Inventory)</h3>
        {lowStockItems.noStock.length === 0 ? (
          <p>All products have inventory records.</p>
        ) : (
          <div>
            {/* Desktop Table */}
            <div className="desktop-table">
              <table border="1" cellPadding="8" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th>Item Code</th>
                    <th>Name</th>
                    <th>HSN</th>
                    <th>GST</th>
                    <th>Restock</th>
                  </tr>
                </thead>
                <tbody>
                  {lowStockItems.noStock.map(product => (
                    <tr key={product._id}>
                      <td>{product.itemCode}</td>
                      <td>{product.itemName}</td>
                      <td>{product.hsn}</td>
                      <td>{product.gst}%</td>
                      <td><button onClick={() => prefillRestockForm(product)}>Restock</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Mobile Cards for No Stock */}
            <div className="mobile-cards">
              {lowStockItems.noStock.map(product => (
                <div key={product._id} style={{
                  backgroundColor: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '15px',
                  marginBottom: '15px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                      {product.itemName}
                    </h4>
                    <span style={{ 
                      fontSize: '12px', 
                      padding: '4px 8px', 
                      borderRadius: '4px', 
                      backgroundColor: '#6c757d', 
                      color: 'white',
                      fontWeight: 'bold'
                    }}>
                      No Stock
                    </span>
                  </div>
                  
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                    <strong>Code:</strong> {product.itemCode}
                  </div>
                  
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr', 
                    gap: '10px', 
                    marginBottom: '10px',
                    fontSize: '14px'
                  }}>
                    <div><strong>HSN:</strong> {product.hsn}</div>
                    <div><strong>GST:</strong> {product.gst}%</div>
                  </div>
                  
                  <button 
                    onClick={() => prefillRestockForm(product)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    Restock
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryPanel;
